import React, { useContext, useMemo, useState } from "react";
import TabPanel from "../../TabPanel";
import { SettingsModalTabProps } from "../../types";
import { EssealDataTable } from "esseal-data-table";
import { useQuery, useMutation } from "@apollo/client/react";
import ApprovePermissionModal, { ApprovalOptions } from "./ApprovePermissionModal";
import grantRequestsColumns, { GrantRequestRow } from "./permissionRequestsColumns";
import CurrentUserContext from "@/Contexts/CurrentUser";
import {
  AccessProjectsPickerDocument,
  AccessRegistryDocument,
  GetEmploymentAccessDocument,
  GetGrantRequestsDocument,
  PermissionActionEnum,
  PermissionEntityEnum,
  ResolveGrantRequestDocument,
} from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import { RawDate } from "@/types/globals";
import { filter, find } from "lodash";

const PermissionRequestsTab: React.FC<SettingsModalTabProps> = ({ parentTab, index }) => {
  const { currentUser } = useContext(CurrentUserContext);
  const [selectedRequest, setSelectedRequest] = useState<GrantRequestRow | null>(null);

  const { data: existingPermissions } = useQuery(GetEmploymentAccessDocument, {
    skip: !selectedRequest?.requestorEmployment?.id,
    variables: {
      employmentId: selectedRequest?.requestorEmployment?.id ?? "",
    },
  });

  const existingPerms = existingPermissions?.getEmploymentAccess?.effective.filter(effectivePerms => effectivePerms.entity === selectedRequest?.entity )

  const companyId = currentUser?.defaultCompany?.id;

  const { data, loading } = useQuery(GetGrantRequestsDocument, { skip: !companyId });
  const { data: registryData } = useQuery(AccessRegistryDocument, { fetchPolicy: "cache-first" });
  const { data: projectsData } = useQuery(AccessProjectsPickerDocument, {
    skip: !companyId,
    variables: { companyId: companyId ?? "" },
    fetchPolicy: "cache-first",
  });

  const [resolveRequest] = useMutation(ResolveGrantRequestDocument, {
    refetchQueries: ["getGrantRequests"],
    onCompleted: () => {
      displayNotification({ type: "success", text: "Access request updated successfully" });
    },
    onError: (error) => {
      displayNotification({
        type: "error",
        text: error.message || "Failed to update the access request",
      });
    },
  });

  const projectOptions = useMemo(() => {
    const edges = projectsData?.getProjects?.edges ?? [];
    return edges.flatMap((edge) =>
      edge?.node ? [{ id: String(edge.node.id), title: edge.node.title, code: edge.node.code }] : []
    );
  }, [projectsData]);

  // Only offer project narrowing for modules the registry marks project-scoped.
  const selectedIsProjectScoped = useMemo(() => {
    if (!selectedRequest) return false;
    return Boolean(
      registryData?.accessRegistry?.find((entity) => entity.key === selectedRequest.entity)?.projectScoped
    );
  }, [registryData, selectedRequest]);

  const handleApprove = ({ validTill, projectId, additionalActions }: ApprovalOptions) => {
    if (!selectedRequest) return;

    resolveRequest({
      variables: {
        id: selectedRequest.id,
        approve: true,
        // ISO string out as the schema's opaque date scalar
        validTill: validTill as unknown as RawDate | null,
        projectId,
        additionalActions,
      },
    });
    setSelectedRequest(null);
  };

  const handleReject = (id: string) => {
    resolveRequest({ variables: { id, approve: false } });
  };

  const otherActions = useMemo(() => {
    if (!selectedRequest) return [];

    const registryEntity = find(
      registryData?.accessRegistry || [],
      (entity) => entity.key === selectedRequest.entity
    );
    if (!registryEntity || !registryEntity.actions) return [];

    const projectId = selectedRequest.project?.id ? String(selectedRequest.project.id) : null;

    return filter(registryEntity.actions, (action) => {
      // 0. Exclude View if that is always excluded
      if (action.key === PermissionActionEnum.View) {
        return false;
      }

      // 1. If entity is Projects and action is Create -> skip
      const isProjectEntity =
        selectedRequest.entity === PermissionEntityEnum.Projects;
      const isCreateAction =
        action.key === PermissionActionEnum.Create;

      if (isProjectEntity && isCreateAction) {
        return false;
      }

      // 2. Check if permission already exists in existingPerms
      const foundPerm = find(
        existingPerms || [],
        (perm) =>
          perm.action === action.key &&
          perm.entity === selectedRequest.entity
      );

      if (foundPerm) {
        // If project ID exists and is included in the grant's projectIds -> skip
        if (projectId && foundPerm.projectIds?.map(String).includes(projectId)) {
          return false;
        }

        // If no project ID is in foundPerm
        if (!foundPerm.projectIds || foundPerm.projectIds.length === 0) {
          return false;
        }
      }

      // Otherwise return the action
      return true;
    }) ?? [];
  }, [registryData, selectedRequest, existingPerms]);

  return (
    <TabPanel value={parentTab} index={index} paddingX={1} paddingY={1} height="100%" containerSx={{ height: "100%" }}>
      <EssealDataTable
        rows={data?.getGrantRequests ?? []}
        columns={grantRequestsColumns(setSelectedRequest, handleReject)}
        loading={loading}
        checkboxSelection={false}
        pagination={true}
        pageSize={25}
        getRowId={(row) => row.id}
      />

      {selectedRequest && (
        <ApprovePermissionModal
          open
          requestFor={selectedRequest.requestFor}
          projectScoped={selectedIsProjectScoped}
          requestedProject={
            selectedRequest.project
              ? {
                id: String(selectedRequest.project.id),
                title: selectedRequest.project.title,
                code: "",
              }
              : null
          }
          projectOptions={projectOptions}
          handleClose={() => setSelectedRequest(null)}
          onApprove={handleApprove}
          otherActions={otherActions}
        />
      )}
    </TabPanel>
  );
};

export default PermissionRequestsTab;
